/**
 * Enhanced JSON Tree Viewer Component
 * Uses react-json-view for better collapsible/expandable JSON display
 */
import React, { useEffect, useState } from "react";

import {
  CopyAll as CopyAllIcon,
  DataObject as DataObjectIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ReactJson, { InteractionProps, ThemeKeys } from "react-json-view";

interface JsonTreeViewerProps {
  data: unknown;
  title?: string;
  maxHeight?: number;
  defaultCollapsed?: boolean;
  enableCopy?: boolean;
  enableEdit?: boolean;
  onEdit?: (editInfo: InteractionProps) => void;
}

const THEME_OPTIONS: Array<{ value: ThemeKeys; label: string }> = [
  { value: "rjv-default", label: "Default" },
  { value: "monokai", label: "Monokai" },
  { value: "apathy", label: "Apathy" },
  { value: "apathy:inverted", label: "Apathy Inverted" },
  { value: "ashes", label: "Ashes" },
  { value: "bespin", label: "Bespin" },
  { value: "brewer", label: "Brewer" },
  { value: "bright", label: "Bright" },
  { value: "bright:inverted", label: "Bright Inverted" },
  { value: "chalk", label: "Chalk" },
  { value: "codeschool", label: "Code School" },
  { value: "colors", label: "Colors" },
  { value: "eighties", label: "Eighties" },
  { value: "embers", label: "Embers" },
  { value: "flat", label: "Flat" },
  { value: "google", label: "Google" },
  { value: "grayscale", label: "Grayscale" },
  { value: "grayscale:inverted", label: "Grayscale Inverted" },
  { value: "greenscreen", label: "Green Screen" },
  { value: "harmonic", label: "Harmonic" },
  { value: "hopscotch", label: "Hopscotch" },
  { value: "isotope", label: "Isotope" },
  { value: "marrakesh", label: "Marrakesh" },
  { value: "mocha", label: "Mocha" },
  { value: "ocean", label: "Ocean" },
  { value: "paraiso", label: "Paraiso" },
  { value: "pop", label: "Pop" },
  { value: "railscasts", label: "Rails Casts" },
  { value: "shapeshifter", label: "Shapeshifter" },
  { value: "solarized", label: "Solarized" },
  { value: "summerfruit", label: "Summer Fruit" },
  { value: "summerfruit:inverted", label: "Summer Fruit Inverted" },
  { value: "threezerotwofour", label: "Threezerotwofour" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "tube", label: "Tube" },
  { value: "twilight", label: "Twilight" },
];

export const JsonTreeViewer: React.FC<JsonTreeViewerProps> = ({
  data,
  title,
  maxHeight = 600,
  defaultCollapsed = false,
  enableCopy = true,
  enableEdit = false,
  onEdit,
}) => {
  const muiTheme = useTheme();
  const [theme, setTheme] = useState<ThemeKeys>(
    muiTheme.palette.mode === "dark" ? "monokai" : "rjv-default"
  );
  const [collapsed, setCollapsed] = useState<boolean | number>(
    typeof defaultCollapsed === "number"
      ? defaultCollapsed
      : defaultCollapsed
      ? 1
      : 2
  );
  const [displayDataTypes, setDisplayDataTypes] = useState(false);
  const [displayObjectSize, setDisplayObjectSize] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [indentWidth, setIndentWidth] = useState(4);
  const [expandLevel, setExpandLevel] = useState(2);
  const [iconStyle, setIconStyle] = useState<"circle" | "triangle" | "square">(
    "triangle"
  );
  const [enableClipboard, setEnableClipboard] = useState(enableCopy);
  const [copySuccess, setCopySuccess] = useState(false);

  // Auto-adjust theme based on MUI theme
  useEffect(() => {
    setTheme(muiTheme.palette.mode === "dark" ? "monokai" : "rjv-default");
  }, [muiTheme.palette.mode]);

  const getDataStats = () => {
    if (data === null || data === undefined) return { type: "null", size: 0 };

    const type = Array.isArray(data) ? "array" : typeof data;
    let size = 0;

    if (Array.isArray(data)) {
      size = data.length;
    } else if (typeof data === "object" && data !== null) {
      size = Object.keys(data as Record<string, unknown>).length;
    }

    return { type, size };
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleEdit = (editInfo: InteractionProps) => {
    if (onEdit) {
      onEdit(editInfo);
    }
  };

  const stats = getDataStats();

  if (data === null || data === undefined) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography>No data to display</Typography>
      </Alert>
    );
  }

  // Ensure data is serializable for ReactJson
  const jsonData = (() => {
    try {
      // Convert to JSON and back to ensure it's serializable
      return JSON.parse(JSON.stringify(data));
    } catch (error) {
      // If data can't be serialized, convert to string representation
      return { value: String(data), type: typeof data };
    }
  })();

  return (
    <Box sx={{ width: "100%", textAlign: "left" }}>
      {/* Header - only show if title is provided */}
      {title && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DataObjectIcon color="primary" />
            <Typography variant="h6">{title}</Typography>
            <Chip
              label={`${stats.type}${stats.size > 0 ? ` (${stats.size})` : ""}`}
              size="small"
              variant="outlined"
              color="primary"
            />
          </Box>

          <Stack direction="row" spacing={1}>
            {enableCopy && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<CopyAllIcon />}
                onClick={handleCopyToClipboard}
                color={copySuccess ? "success" : "primary"}
              >
                {copySuccess ? "Copied!" : "Copy"}
              </Button>
            )}

            <Button
              size="small"
              variant="outlined"
              startIcon={
                (typeof collapsed === "number" && collapsed <= 1) ||
                collapsed === true ? (
                  <VisibilityIcon />
                ) : (
                  <VisibilityOffIcon />
                )
              }
              onClick={() => {
                if (typeof collapsed === "number") {
                  setCollapsed(collapsed <= 1 ? 3 : 1);
                } else {
                  setCollapsed(!collapsed);
                }
              }}
            >
              {(typeof collapsed === "number" && collapsed <= 1) ||
              collapsed === true
                ? "Expand All"
                : "Collapse All"}
            </Button>

            <Button
              size="small"
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setShowSettings(!showSettings)}
            >
              Settings
            </Button>
          </Stack>
        </Box>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle2" gutterBottom>
            Display Settings
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: 2,
            }}
          >
            <FormControl size="small">
              <InputLabel>Theme</InputLabel>
              <Select
                value={theme}
                label="Theme"
                onChange={(e) => setTheme(e.target.value as ThemeKeys)}
              >
                {THEME_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Icon Style</InputLabel>
              <Select
                value={iconStyle}
                label="Icon Style"
                onChange={(e) =>
                  setIconStyle(
                    e.target.value as "circle" | "triangle" | "square"
                  )
                }
              >
                <MenuItem value="triangle">Triangle</MenuItem>
                <MenuItem value="circle">Circle</MenuItem>
                <MenuItem value="square">Square</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography variant="caption" display="block" gutterBottom>
                Indent Width: {indentWidth}
              </Typography>
              <Slider
                value={indentWidth}
                onChange={(_, value) => setIndentWidth(value as number)}
                min={1}
                max={10}
                step={1}
                size="small"
              />
            </Box>

            <Box>
              <Typography variant="caption" display="block" gutterBottom>
                Default Expand Level: {expandLevel}
              </Typography>
              <Slider
                value={expandLevel}
                onChange={(_, value) => {
                  setExpandLevel(value as number);
                  setCollapsed(value as number);
                }}
                min={0}
                max={5}
                step={1}
                size="small"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={displayDataTypes}
                  onChange={(e) => setDisplayDataTypes(e.target.checked)}
                  size="small"
                />
              }
              label="Show Data Types"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={displayObjectSize}
                  onChange={(e) => setDisplayObjectSize(e.target.checked)}
                  size="small"
                />
              }
              label="Show Object Size"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={enableClipboard}
                  onChange={(e) => setEnableClipboard(e.target.checked)}
                  size="small"
                />
              }
              label="Enable Clipboard"
            />
          </Box>
        </Paper>
      )}

      {/* JSON Tree */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          bgcolor: "background.default",
          maxHeight: maxHeight,
          overflow: "auto",

          "& .react-json-view": {
            fontFamily: "Consolas, Monaco, 'Courier New', monospace",
            fontSize: "0.875rem",
            lineHeight: 1.5,
          },
          "& .react-json-view > div": {},
          "& .object-content": {},
          "& .array-content": {},
        }}
      >
        <ReactJson
          src={jsonData}
          theme={theme}
          collapsed={collapsed}
          displayDataTypes={displayDataTypes}
          displayObjectSize={displayObjectSize}
          enableClipboard={enableClipboard}
          indentWidth={indentWidth}
          iconStyle={iconStyle}
          onEdit={enableEdit ? handleEdit : undefined}
          onAdd={enableEdit ? handleEdit : undefined}
          onDelete={enableEdit ? handleEdit : undefined}
          name={false}
          quotesOnKeys={false}
          sortKeys={false}
          collapseStringsAfterLength={100}
          shouldCollapse={(field) => {
            // Custom collapse logic for automatic collapsing
            const src = field.src as unknown;
            const depth = field.namespace?.length || 0;

            // Collapse very deep nested objects
            if (depth > 3) {
              return true;
            }

            // Collapse long strings
            if (typeof src === "string" && src.length > 200) {
              return true;
            }

            // Collapse large arrays
            if (Array.isArray(src) && src.length > 10) {
              return true;
            }

            // Collapse large objects
            if (src && typeof src === "object" && Object.keys(src).length > 8) {
              return true;
            }

            return false;
          }}
        />
      </Paper>
    </Box>
  );
};
